import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Checkbox, Select } from '@ferris/react/components/forms';
import addressFormConfig from '@homeaway/stab-address-form-configurator';

import LocationField from '../AutoComplete/LocationField';
import { ADDRESS_FIELDS, keysToFields } from '../AutoComplete/AddressFormats';
import { retrieveData } from '../../../../actions/graphQlSvc';
import {
    FETCH_ADDRESS_FORM_CONFIGURATION, getFieldOrder,
    getFieldOrderV2
} from './util';
import { ExperimentCtx } from '../../../../contexts/experiments';
import { EGTNL_TESTS_MPAAS } from '../../../../../server/api/abacus/constants';

export const getFormInput = (fieldLabelKey, label, value, changeHandler, error, brand) => (
    <LocationField
        key={fieldLabelKey}
        fieldLabelKey={fieldLabelKey}
        fieldLabelTranslation={label}
        value={value}
        onChangeHandler={changeHandler}
        errorMessage={error}
        classes={`form-group ${fieldLabelKey} ${brand}`}
    />
);

function FieldR(props) {
    const {
        value,
        fieldError,
        addressFieldKey,
        brand,
        labelTranslation,
        onChange,
        isAddressV2
    } = props;

    const [visited, setVisited] = useState(false);
    const onBlur = () => {
        setVisited(true);
    };

    const [error, setError] = useState('');

    useEffect(() => {
        if (!isAddressV2 || visited || addressFieldKey === ADDRESS_FIELDS.ADDRESS2) {
            setError(fieldError);
        }
    }, [fieldError, visited]);

    let ComponentToUse = LocationField;
    const baseProps = {
        key: addressFieldKey,
        fieldLabelKey: addressFieldKey,
        fieldLabelTranslation: labelTranslation,
        value,
        onChangeHandler: onChange,
        errorMessage: error,
        classes: `form-group ${addressFieldKey} ${brand}`,
        onChange,
        label: labelTranslation,
        onBlur
    };
    const customProps = {};
    switch (addressFieldKey) {
    case ADDRESS_FIELDS.COUNTRY:
    case ADDRESS_FIELDS.STATE_PROVINCE:
        // eslint-disable-next-line no-case-declarations
        const { countryList, statesList } = props;
        // eslint-disable-next-line no-case-declarations
        const isState = addressFieldKey === ADDRESS_FIELDS.STATE_PROVINCE;
        // eslint-disable-next-line max-len
        if (isState && !(statesList && statesList.length > 1)) break;
        ComponentToUse = Select;
        customProps.fullWidth = true;
        customProps.name = addressFieldKey;
        customProps.formControlProps = { id: `${addressFieldKey}` };
        customProps.invalid = error.length;
        customProps.floatLabel = true;
        customProps.isControlled = true;
        customProps.type = 'text';
        customProps.validationMessage = error || '';
        customProps.optionList = isState ? statesList : countryList;
        break;
    case ADDRESS_FIELDS.ADDRESS2:
        // eslint-disable-next-line no-case-declarations
        const {
            displayUNCheckbox, setConfirmNoUnitNumber, noUnitTranlsation, confirmNoUnitNumber
        } = props;

        if (!displayUNCheckbox()) break;
        // eslint-disable-next-line no-case-declarations
        const handleCheck = (e) => {
            setConfirmNoUnitNumber(e.target.checked);
        };
        return (
            <div className="input-checkbox">
                <LocationField
                    {
                        ...{
                            ...baseProps,
                            errorMessage: confirmNoUnitNumber ? '' : error
                        }
                    }
                />
                <Checkbox
                    name="no-unit-number-confirmation"
                    label={noUnitTranlsation}
                    onChange={handleCheck}
                />
            </div>
        );
    default:
        break;
    }
    return (
        <ComponentToUse
            {
                ...{
                    ...baseProps,
                    ...customProps
                }
            }
        />
    );
}

FieldR.propTypes = {
    value: PropTypes.string.isRequired,
    fieldError: PropTypes.string.isRequired,
    addressFieldKey: PropTypes.string.isRequired,
    brand: PropTypes.string.isRequired,
    labelTranslation: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    countryList: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string
    })).isRequired,
    statesList: PropTypes.arrayOf(PropTypes.shape({
        text: PropTypes.string,
        value: PropTypes.string
    })),
    setConfirmNoUnitNumber: PropTypes.func.isRequired,
    displayUNCheckbox: PropTypes.func.isRequired,
    noUnitTranlsation: PropTypes.string.isRequired,
    confirmNoUnitNumber: PropTypes.bool.isRequired,
    isAddressV2: PropTypes.bool.isRequired
};

FieldR.defaultProps = {
    statesList: null
};

function Fields(props) {
    const {
        unitNumberValidation,
        unitCheckEnabled,
        address,
        countryList,
        addressValidation,
        serverAddressValidation,
        handleAddressFieldChange,
        getTranslation,
        setConfirmNoUnitNumber,
        confirmNoUnitNumber,
        brand,
        locale,
        isConventionalLodgingFlow,
        isAddressV2,
        isVacasa
    } = props;

    const { country: selectedCountry } = address;
    const { experimentEvaluations } = useContext(ExperimentCtx) || {};
    const {
        [EGTNL_TESTS_MPAAS.ULX_ADDRESS_FORM_CONFIGURATION_VR]: AddressFormConfigVRVersion
    } = experimentEvaluations || {};

    // eslint-disable-next-line max-len
    const isAddressConfigurationUsed = !isVacasa && (isConventionalLodgingFlow
        || AddressFormConfigVRVersion === 1);
    const [addressFormConfiguration, setAddressFormConfiguration] = useState({});

    const { order, ignoreRTL } = isAddressConfigurationUsed
        ? getFieldOrderV2(addressFormConfiguration, selectedCountry)
        : getFieldOrder(selectedCountry);

    const [statesList, setStatesList] = useState(null);

    const fetchAddressFormConfiguration = async (countryCode) => {
        try {
            const addressFormConfigCall = (
                await retrieveData(FETCH_ADDRESS_FORM_CONFIGURATION, {
                    countryCode,
                    locale
                })
            ).data.addressFormConfiguration;
            setAddressFormConfiguration(addressFormConfigCall);
            // Do something with addressFormConfigCall if needed
        } catch (error) {
            setAddressFormConfiguration({});
        }
    };
    useEffect(() => {
        if (isAddressConfigurationUsed) {
            // load fields configuration for the selected country
            fetchAddressFormConfiguration(selectedCountry);
        }
        const states = addressFormConfig.getRegions((selectedCountry || ''));
        const list = states
        && Object.keys(states).map((region) => ({
            text: states[region],
            value: region
        }));
        if (list) list.unshift({ text: '---', value: '' });
        setStatesList(list);
    }, [selectedCountry]);

    const getFieldTranslation = (suffix) => getTranslation(`Vrbo.lyp.manualLocation.${suffix}`);

    // eslint-disable-next-line max-len
    const displayUNCheckbox = () => (unitNumberValidation.value.missingUnitNumber || !unitNumberValidation.value.unitNumberValid)
    && !unitNumberValidation.start
    && unitNumberValidation.success
    && unitCheckEnabled;

    useEffect(() => {
        if (isAddressConfigurationUsed && Object.keys(addressFormConfiguration).length && selectedCountry) {
            const {
                country: countryName,
                city: cityConfig,
                stateProvince: stateProvinceConfig,
                postalCode: postalCodeConfig
            } = addressFormConfiguration;
            // there are only two cases, singapore and BV island, we default to country name
            if (cityConfig?.displayStatus === 'HIDDEN') {
                handleAddressFieldChange({ target: { name: ADDRESS_FIELDS.CITY, value: countryName } });
            }
            // unsets the stateProvince field when it is hidden
            if (stateProvinceConfig?.displayStatus === 'HIDDEN') {
                handleAddressFieldChange({ target: { name: ADDRESS_FIELDS.STATE_PROVINCE, value: '' } });
            }
            // sets the postal code to 00000 when it is hidden
            if (postalCodeConfig?.displayStatus === 'HIDDEN') {
                handleAddressFieldChange({ target: { name: ADDRESS_FIELDS.POSTAL_CODE, value: '00000' } });
            }
        }
    }, [addressFormConfiguration]);

    const classes = cn('form-row', { 'form-row-ignore-rtl': ignoreRTL });
    const rows = order.map((row) => {
        const items = row.map((fieldLabelKey) => {
            const addressFieldKey = keysToFields[fieldLabelKey];
            return (
                <FieldR
                    addressFieldKey={addressFieldKey}
                    value={address[addressFieldKey] || ''}
                    fieldError={addressValidation[addressFieldKey].error || serverAddressValidation[addressFieldKey]?.error || ''}
                    key={fieldLabelKey}
                    labelTranslation={
                        isAddressConfigurationUsed ? addressFormConfiguration[addressFieldKey]?.label
                            : getFieldTranslation(fieldLabelKey) || ''
                    }
                    onChange={handleAddressFieldChange}
                    countryList={countryList}
                    statesList={statesList}
                    brand={brand}
                    setConfirmNoUnitNumber={setConfirmNoUnitNumber}
                    displayUNCheckbox={displayUNCheckbox}
                    noUnitTranlsation={getFieldTranslation(address[addressFieldKey] ? 'unitNumberConfirmation' : 'noUnitConfirmation')}
                    confirmNoUnitNumber={confirmNoUnitNumber}
                    isAddressV2={isAddressV2}
                />
            );
        });
        return (
            <div key={row[0]}>
                <div className={classes}>
                    {items}
                </div>
                <div className="break" />
            </div>
        );
    });
    return (
        <div className="ulx-form-area">
            {
                // country field is rendered outside of the order loop
                isAddressConfigurationUsed && (
                    <>
                        <div className={classes}>
                            <FieldR
                                addressFieldKey={ADDRESS_FIELDS.COUNTRY}
                                value={address[ADDRESS_FIELDS.COUNTRY] || ''}
                                fieldError={addressValidation[ADDRESS_FIELDS.COUNTRY].error || serverAddressValidation[ADDRESS_FIELDS.COUNTRY]?.error || ''}
                                key={ADDRESS_FIELDS.COUNTRY}
                                labelTranslation={isAddressConfigurationUsed
                                    ? addressFormConfiguration?.countryField?.label
                                    : getFieldTranslation(ADDRESS_FIELDS.COUNTRY) || ''}
                                onChange={handleAddressFieldChange}
                                countryList={countryList}
                                statesList={statesList}
                                brand={brand}
                                setConfirmNoUnitNumber={setConfirmNoUnitNumber}
                                displayUNCheckbox={displayUNCheckbox}
                                noUnitTranlsation={getFieldTranslation(address[ADDRESS_FIELDS.COUNTRY] ? 'unitNumberConfirmation' : 'noUnitConfirmation')}
                                confirmNoUnitNumber={confirmNoUnitNumber}
                                isAddressV2={isAddressV2}
                            />
                        </div>
                        <div className="break" />
                    </>
                )
            }
            {
                (selectedCountry || !isAddressConfigurationUsed) && rows
            }
        </div>
    );
}

Fields.propTypes = {
    isVacasa: PropTypes.bool.isRequired,
    unitNumberValidation: PropTypes.shape({
        value: PropTypes.bool,
        start: PropTypes.bool,
        success: PropTypes.bool,
        fail: PropTypes.bool
    }).isRequired,
    unitCheckEnabled: PropTypes.bool.isRequired,
    serverAddressValidation: PropTypes.shape({
        address1: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        address2: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        city: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        stateProvince: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        postalCode: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        country: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        generalState: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            errorList: PropTypes.arrayOf(PropTypes.string)
        }),
        pinMap: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string
        })
    }).isRequired,
    addressValidation: PropTypes.shape({
        address1: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        address2: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        city: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        stateProvince: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        postalCode: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        country: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string
        })
    }).isRequired,
    address: PropTypes.shape({
        address1: PropTypes.string,
        address2: PropTypes.string,
        city: PropTypes.string,
        stateProvince: PropTypes.string,
        postalCode: PropTypes.string,
        country: PropTypes.string
    }).isRequired,
    countryList: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string
    })).isRequired,
    handleAddressFieldChange: PropTypes.func.isRequired,
    getTranslation: PropTypes.func.isRequired,
    setConfirmNoUnitNumber: PropTypes.func.isRequired,
    confirmNoUnitNumber: PropTypes.bool.isRequired,
    brand: PropTypes.string.isRequired,
    locale: PropTypes.string.isRequired,
    isConventionalLodgingFlow: PropTypes.bool.isRequired,
    isAddressV2: PropTypes.bool.isRequired
};

export default Fields;
